// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useBulk from "@/hooks/useBulk";
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import usePageSize from "@/hooks/usePageSize";
import usePopup from "@/hooks/usePopup";
import useSelection from "@/hooks/useSelection";
import useTranslation from "@/hooks/useTranslation";
import type { TyTrashItem, TyTrashPage } from "@/types";
import { formatDate, pressToOpen, readLangValue, rightClickToOpen } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import type { TyIconName } from "@/UI/atoms/Icon/types";
import StatusChip from "@/UI/atoms/StatusChip";
import ActionsBar from "@/UI/components/ActionsBar";
import BulkBar from "@/UI/components/BulkBar";
import EmptyState from "@/UI/components/EmptyState";
import Paginator from "@/UI/components/Paginator";
import ActionsPopup, { type TyAction } from "@/UI/popups/ActionsPopup";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import RestoreConflictPopup from "@/UI/popups/RestoreConflictPopup";
// The rows are picked like the card lists are, so they wear the same ring.
import "@/UI/cards/selection.css";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
/** What each kind of record looks like in the list. */
const icons: Record<string, TyIconName> = {
    Project: "properties",
    Unit: "building",
    Customer: "crm",
    Lead: "crm",
    Contract: "document",
    Reservation: "document",
    InstallmentPlan: "subscription",
    Installment: "subscription",
    Warranty: "crown",
};

/** Whole days from now until the sweep takes it. */
const daysLeft = (clearsAt: string | null): number | null => {
    if (!clearsAt) return null;
    return Math.max(0, Math.ceil((new Date(clearsAt).getTime() - Date.now()) / (24 * 60 * 60 * 1000)));
};

/**
 * What this organization has thrown away, and the two things it can do about it.
 *
 * Deleting is open to every role in this product precisely because it is reversible — this screen is
 * the half that makes that true. Each row says when it will be cleared for good, because that is the
 * only question somebody opening the trash actually has.
 *
 * Owner and manager only, enforced by the API's segment list as well: deciding what is gone for ever
 * is not operational work.
 */
export default function TrashScreen() {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const addComp = usePopup((state) => state.addComp);
    const [params, setParams] = useSearchParams();
    const pageSize = usePageSize((state) => state.pageSize);

    const model = params.get("model") ?? "";
    const page = Math.max(1, Number(params.get("page")) || 1);
    /**
     * Which row was acted on last. Paired with the request's own `loading` below rather than cleared
     * by an effect — "is this row busy" is a question the request can already answer.
     */
    const [pending, setPending] = useState<string | null>(null);

    const trash = useFetch<TyTrashPage>("/trash", {}, false);
    const action = useFetch("/trash", {}, false);
    const selection = useSelection();

    const load = () => trash.invoke({ query: { ...(model ? { model } : {}), limit: pageSize, page } });

    /**
     * The one list whose selection is not bare ids: its rows come from nine collections, so a row is
     * `{ model, id }` and an id alone does not say which collection to look in. The ids are kept as
     * `Model:id` and split back apart here.
     */
    const bulk = useBulk({
        path: "/trash",
        selection,
        onDone: load,
        body: () => ({ items: selection.ids.map((key) => ({ model: key.split(":")[0], id: key.split(":")[1] })) }),
    });

    useEffect(() => {
        load();
        // A different kind, or a different page, is a different list — its picks go with it.
        selection.clear();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [model, page, pageSize]);

    useEffect(() => {
        if (!action.succeededAt) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action.succeededAt]);

    /**
     * A customer whose phone is taken cannot simply be refused with a sentence: the clash means two
     * records for one person, and only somebody looking at both can say which. The API names the
     * live one in the refusal, so the screen opens it rather than printing the message and stopping.
     */
    useEffect(() => {
        const conflict = (action.errorData as { customer?: { _id: string; name: string; phone: string } } | null)?.customer;
        if (!conflict || !pending) return;

        addComp({
            name: "restore-conflict",
            Component: RestoreConflictPopup,
            anim: "scale",
            details: { id: pending, customer: conflict, onDone: load },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action.errorData]);

    const isBusy = (id: string) => action.loading && pending === id;
    const data = trash.data;
    const retention = data?.retentionDays ?? 30;

    const restore = (item: TyTrashItem) => {
        setPending(item._id);
        action.invoke({ method: "POST", path: `/trash/${item.model}/${item._id}/restore` });
    };

    const purge = (item: TyTrashItem, name: string) =>
        addComp({
            name: "confirm",
            Component: ConfirmPopup,
            anim: "scale",
            details: {
                title: t.trash.purge,
                message: t.trash.confirmPurge.replace("{name}", name),
                confirmLabel: t.trash.purge,
                onConfirm: () => {
                    setPending(item._id);
                    action.invoke({ method: "DELETE", path: `/trash/${item.model}/${item._id}` });
                },
            },
        });

    /**
     * The row's menu. It exists because `select` needed somewhere to live and the row had only two
     * buttons — so the destructive one moved behind the menu with it, the same move the share action
     * made on the property cards. Restore stays in the open: it is what the screen is for.
     */
    const openActions = (item: TyTrashItem, name: string, anchor: DOMRect) => {
        const actions: TyAction[] = [
            { key: "select", label: t.bulk.select, icon: "check", onSelect: () => selection.toggle(`${item.model}:${item._id}`) },
            { key: "purge", label: t.trash.purge, icon: "trash", danger: true, onSelect: () => purge(item, name) },
        ];

        addComp({
            name: "trash-actions",
            Component: ActionsPopup,
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            details: { actions },
            anchor: { top: anchor.top, left: anchor.left, right: anchor.right, bottom: anchor.bottom, width: anchor.width, height: anchor.height },
        });
    };

    const emptyAll = () =>
        addComp({
            name: "confirm",
            Component: ConfirmPopup,
            anim: "scale",
            details: {
                title: t.trash.emptyNow,
                message: t.trash.confirmEmpty,
                confirmLabel: t.trash.emptyNow,
                // The one irreversible action a client has. It gets the typed confirmation.
                confirmWord: t.trash.emptyWord,
                onConfirm: () => action.invoke({ method: "DELETE", path: "/trash" }),
            },
        });

    /**
     * One tab per kind that actually has something in it, and no "everything" tab.
     *
     * A trash is worked through a kind at a time — restoring three units has nothing to do with the
     * customer deleted beside them — and a mixed list made every row's type something to read rather
     * than something to know from where it sat. Kinds with nothing in them are not offered at all.
     *
     * The counts span the whole trash whatever is filtered for, so the tabs never vanish behind the
     * filter that is using them.
     */
    const tabs = Object.entries(data?.counts ?? {})
        .filter(([, count]) => count > 0)
        .map(([name, count]) => ({ key: name, label: `${t.trash.models[name]} (${count})` }));

    /** Landing with no kind chosen lands on the first that has anything. */
    const active = model || tabs[0]?.key || "";

    return (
        <section className="trash-screen">
            <ActionsBar
                tabs={tabs}
                activeTab={active}
                onTab={(key) => setParams(key ? new URLSearchParams({ model: key }) : new URLSearchParams(), { replace: true })}
                after={
                    data && data.total > 0 ? (
                        <button type="button" className="trash-screen__empty" onClick={emptyAll} disabled={action.loading}>
                            <Icon name="trash" size={16} />
                            <span>{t.trash.emptyNow}</span>
                        </button>
                    ) : undefined
                }
            />

            <p className="trash-screen__note">{t.trash.retention.replace("{days}", String(retention))}</p>

            {trash.loading && <p className="trash-screen__state">{t.common.loading}</p>}
            {(trash.error || action.error) && <p className="trash-screen__state trash-screen__state--error">{trash.error ?? action.error}</p>}
            {!trash.loading && !trash.error && !data?.items.length && (
                <EmptyState icon="trash" title={t.trash.empty} description={t.trash.emptyHint.replace("{days}", String(retention))} />
            )}

            <div className="trash-screen__list">
                {data?.items
                    .filter((item) => !active || item.model === active)
                    .map((item) => {
                        const name = readLangValue(item.label, lang) || t.properties.unnamed;
                        const left = daysLeft(item.clearsAt);

                        const key = `${item.model}:${item._id}`;
                        const isPicked = selection.has(key);

                        return (
                            <article
                                key={key}
                                className={`trash-screen__row pick${isPicked ? " pick--on" : ""}${selection.isSelecting ? " pick--picking" : ""}`}
                                // While the bar is up the row picks rather than sits there — the same
                                // gesture the card lists use, and the bar's cancel is the way out.
                                role={selection.isSelecting ? "button" : undefined}
                                tabIndex={selection.isSelecting ? 0 : undefined}
                                aria-pressed={selection.isSelecting ? isPicked : undefined}
                                onClick={selection.isSelecting ? () => selection.toggle(key) : undefined}
                                onKeyDown={selection.isSelecting ? pressToOpen(() => selection.toggle(key)) : undefined}
                                onContextMenu={selection.isSelecting ? undefined : rightClickToOpen((anchor) => openActions(item, name, anchor))}
                            >
                                <span className="trash-screen__icon">
                                    <Icon name={icons[item.model] ?? "document"} size={20} />
                                </span>

                                <span className="trash-screen__identity">
                                    <strong className="trash-screen__name">{name}</strong>
                                    <span className="trash-screen__meta">
                                        {t.trash.models[item.model]} · {t.trash.deletedAt} {formatDate(item.deletedAt, lang)}
                                    </span>
                                </span>

                                {left !== null && (
                                    <StatusChip
                                        size="sm"
                                        // Running out is worth noticing; a month away is not.
                                        tone={left <= 3 ? "reserved" : "neutral"}
                                        label={left === 0 ? t.trash.clearsToday : t.trash.clearsIn.replace("{days}", String(left))}
                                    />
                                )}

                                {/* While the bar is up the row means one thing: both of its buttons
                                    are gone and the tick takes the menu's place. */}
                                <div className="trash-screen__actions">
                                    {selection.isSelecting ? (
                                        <span className={`pick__box${isPicked ? " pick__box--on" : ""}`} aria-hidden="true">
                                            <Icon name="check" size={18} />
                                        </span>
                                    ) : (
                                        <>
                                            <button
                                                type="button"
                                                className="trash-screen__restore"
                                                onClick={() => restore(item)}
                                                disabled={isBusy(item._id)}
                                            >
                                                <Icon name="refresh" size={15} />
                                                <span>{t.trash.restore}</span>
                                            </button>
                                            <button
                                                type="button"
                                                className="trash-screen__more"
                                                onClick={(event) => openActions(item, name, event.currentTarget.getBoundingClientRect())}
                                                aria-label={t.common.more}
                                                title={t.common.more}
                                            >
                                                <Icon name="more" size={15} />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </article>
                        );
                    })}
            </div>

            {/**
             * No fields: there is nothing on a thrown-away record to edit. Its two actions are the
             * two the screen has — and both are the bar's, so a cascade of forty units is forty
             * restores in one press instead of forty.
             */}
            {selection.isSelecting ? (
                <BulkBar
                    count={selection.count}
                    busy={bulk.busy}
                    note={bulk.note}
                    onClear={selection.clear}
                    extras={[
                        {
                            key: "restore",
                            label: t.bulk.restoreMany,
                            icon: "refresh",
                            onSelect: () =>
                                bulk.confirmed({
                                    title: t.bulk.restoreMany,
                                    message: t.bulk.confirmRestore.replace("{count}", String(selection.count)),
                                    confirmLabel: t.bulk.restoreMany,
                                    path: "/trash/bulk/restore",
                                    method: "POST",
                                    danger: false,
                                    doneLabel: t.bulk.restored,
                                }),
                        },
                    ]}
                    deleteLabel={t.bulk.purgeMany}
                    onDelete={() =>
                        bulk.confirmed({
                            title: t.bulk.purgeMany,
                            message: t.bulk.confirmPurge.replace("{count}", String(selection.count)),
                            confirmLabel: t.bulk.purgeMany,
                            // The one delete with no way back gets the typed confirmation, in bulk
                            // most of all — forty records at once is forty times the mistake.
                            word: t.trash.emptyWord,
                            doneLabel: t.bulk.purged,
                        })
                    }
                />
            ) : (
                <Paginator
                    page={page}
                    total={trash.data?.total ?? 0}
                    count={trash.data?.items.length ?? 0}
                    onPage={(next) => {
                        const merged = new URLSearchParams(params);
                        if (next === 1) merged.delete("page");
                        else merged.set("page", String(next));
                        setParams(merged, { replace: true });
                    }}
                />
            )}
        </section>
    );
}
